import {
  DAILY_PRIZE_MODAL_VISIBLE,
  END_OF_GAME_MODAL_VISIBLE,
  GAME_RESULTS_MODAL_VISIBLE
} from '../constants/config';
import DailyPrizeModal from './DailyPrizeModal';
import { GameResultsModal } from './GameResultsModal';
import { EndOfGameModal } from './EndOfGameModal';
import YettelPetakWizardModal from './YettelPetakWizardModal';
import { useSearchParams } from 'react-router-dom';
import { SEARCH_PARAMS } from '../constants/searchParamNames';
// import QuestionnaireModal from './Questionnaire.tsx';
import { ModalID } from '../config/modalQueueConfig.ts';
import { useCallback, useMemo, useState } from 'react';
import { useFetchBanners } from '../hooks/useFetchBanners.ts';
import { BannerType } from '../constants/banners.ts';
import { Banner, ScratchOffer, ScratchOfferResponse } from '../types/types.ts';
import { SESSION_STORAGE_KEYS } from '../constants/sessionStorageKeys.ts';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../constants/queryKeys.ts';
import { callPublicAPI } from '../utils/api.ts';
import { isUserLoggedIn } from '../utils/auth.ts';
import { UNUSED } from '../constants/scratchCard.ts';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorageKeys.ts';

const Modals = () => {
  const [searchParams] = useSearchParams();
  const jweToken = searchParams.get(SEARCH_PARAMS.TOKEN);
  const isHidden = searchParams.get(SEARCH_PARAMS.HIDE_HEADER_FOOTER) === '1';
  const [currentIndex, setCurrentIndex] = useState(0);

  const isLoggedIn = isUserLoggedIn();

  const { data: scratchOffer } = useQuery<ScratchOfferResponse>({
    queryKey: [QUERY_KEYS.GET_SCRATCH_OFFERS],
    // Background eligibility check that only gates an optional wizard modal. A
    // 403 here is an expected state (e.g. the user isn't truly logged in / not
    // eligible), so it must not raise the global error toast and isn't worth a
    // Sentry report. Any other failure just leaves the modal hidden.
    meta: { skipGlobalErrorToast: true },
    retry: false,
    queryFn: async () => {
      const response = await callPublicAPI<ScratchOffer>(
        'GET',
        '/scratch-offers',
        null,
        undefined,
        undefined,
        undefined,
        { shouldReport: info => info.status !== 403 }
      );
      return response;
    },
    enabled: isLoggedIn
  });

  const handleClose = useCallback(() => {
    setCurrentIndex(prev => prev + 1);
  }, []);

  const { data: banners } = useFetchBanners();
  const dailyPrizeBanner = banners?.find(
    b => b.bannerType === BannerType.popup
  );

  const modalSequence = useMemo(() => {
    const modals: { id: ModalID; payload?: Banner }[] = [];

    if (
      scratchOffer?.data?.status === UNUSED &&
      sessionStorage.getItem(
        SESSION_STORAGE_KEYS.IS_WIZARD_YETTEL_PETAK_MODAL_SEEN
      ) !== 'true'
    ) {
      modals.push({ id: ModalID.YETTEL_WIZARD });
    }

    let shouldShowDailyPrizeModal = false;
    if (dailyPrizeBanner && DAILY_PRIZE_MODAL_VISIBLE === 'true') {
      if (dailyPrizeBanner.showEveryTime === 1) {
        if (
          sessionStorage.getItem(
            SESSION_STORAGE_KEYS.IS_PRIZE_MODAL_SEEN + dailyPrizeBanner.id
          ) !== 'true'
        ) {
          shouldShowDailyPrizeModal = true;
        }
      } else if (dailyPrizeBanner.showEveryTime === 0) {
        if (
          localStorage.getItem(
            LOCAL_STORAGE_KEYS.IS_PRIZE_MODAL_SEEN + dailyPrizeBanner.id
          ) !== 'true'
        ) {
          shouldShowDailyPrizeModal = true;
        }
      }
    }

    if (shouldShowDailyPrizeModal) {
      modals.push({ id: ModalID.DAILY_PRIZE, payload: dailyPrizeBanner });
    }

    if (
      sessionStorage.getItem(SESSION_STORAGE_KEYS.IS_ENDGAME_MODAL_SEEN) !==
        'true' &&
      END_OF_GAME_MODAL_VISIBLE === 'true'
    ) {
      modals.push({ id: ModalID.END_OF_GAME });
    }

    if (
      sessionStorage.getItem(SESSION_STORAGE_KEYS.IS_RESULTS_MODAL_SEEN) !==
        'true' &&
      GAME_RESULTS_MODAL_VISIBLE === 'true'
    ) {
      modals.push({ id: ModalID.GAME_RESULTS });
    }

    modals.push({ id: ModalID.NEWSLETTER });

    return modals;
    // eslint-disable-next-line
  }, [dailyPrizeBanner]);

  if (jweToken || isHidden) return null;

  const currentModal = modalSequence[currentIndex];

  const renderModal = () => {
    if (!currentModal) return null;

    const { id, payload } = currentModal;

    switch (id) {
      case ModalID.YETTEL_WIZARD:
        return <YettelPetakWizardModal scratchOffer={scratchOffer} />;
      case ModalID.DAILY_PRIZE:
        return <DailyPrizeModal banner={payload} onClose={handleClose} />;
      case ModalID.END_OF_GAME:
        return <EndOfGameModal onClose={handleClose} />;
      case ModalID.GAME_RESULTS:
        return <GameResultsModal onClose={handleClose} />;
      // case ModalID.QUESTIONNAIRE:
      //   return <QuestionnaireModal onClose={handleClose} />;
      // Add NewsletterModal when available
      default:
        return null;
    }
  };

  return <>{renderModal()}</>;
};

export default Modals;
